import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { getPropiedades, getCachedPropiedades } from './services/propiedadesService';
import TestimonialsSection from './components/TestimonialsSection';

/* ================================================================
   CASAS MARIA ERNESTINA — ALOJAMIENTOS VACACIONALES EN HUATULCO
   Atención personal, trato directo y facilitación de estancias
   ================================================================ */

// Helper para asignar iconos de FontAwesome según la amenidad
function getAmenityIcon(amenity) {
  const text = String(amenity).toLowerCase();
  if (text.includes('wifi') || text.includes('internet')) return 'fa-wifi';
  if (text.includes('alberca') || text.includes('piscina') || text.includes('pool')) return 'fa-water-ladder';
  if (text.includes('aire') || text.includes('a/c') || text.includes('clima') || text.includes('climatizada')) return 'fa-snowflake';
  if (text.includes('cocina') || text.includes('kitchen') || text.includes('refrigerador') || text.includes('cafetera') || text.includes('estufa')) return 'fa-utensils';
  if (text.includes('estacionamiento') || text.includes('cochera') || text.includes('parking') || text.includes('cajón')) return 'fa-square-parking';
  if (text.includes('playa') || text.includes('mar') || text.includes('vista')) return 'fa-umbrella-beach';
  if (text.includes('tv') || text.includes('pantalla') || text.includes('netflix')) return 'fa-tv';
  if (text.includes('lavadora') || text.includes('secadora') || text.includes('lavanderia') || text.includes('servicio')) return 'fa-shirt';
  if (text.includes('seguridad') || text.includes('vigilancia')) return 'fa-shield-halved';
  if (text.includes('jardin') || text.includes('terraza') || text.includes('balcon') || text.includes('patio')) return 'fa-tree';
  if (text.includes('jacuzzi') || text.includes('tina') || text.includes('hidromasaje')) return 'fa-hot-tub-person';
  if (text.includes('asador') || text.includes('bbq') || text.includes('parrilla')) return 'fa-fire-burner';
  if (text.includes('hamaca')) return 'fa-couch';
  if (text.includes('trabajar') || text.includes('escritorio')) return 'fa-laptop';
  return 'fa-circle-check';
}

// ─────────────── COMPONENTE LIGHTBOX (GALERÍA FULLSCREEN) ───────────────
function Lightbox({ imagenes, indiceInicial, onClose }) {
  const [idx, setIdx] = useState(indiceInicial);

  const next = useCallback(() => setIdx(i => (i + 1) % imagenes.length), [imagenes.length]);
  const prev = useCallback(() => setIdx(i => (i - 1 + imagenes.length) % imagenes.length), [imagenes.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, onClose]);

  return (
    <div className="lightbox-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="lightbox-content" onClick={e => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose} aria-label="Cerrar vista de foto">
          <i className="fas fa-xmark"></i>
        </button>

        <div className="lightbox-main-wrap">
          <img src={imagenes[idx]} alt={`Foto ${idx + 1}`} className="lightbox-img" />
        </div>

        <div className="lightbox-info-bar">
          <span className="lightbox-counter">Foto {idx + 1} de {imagenes.length}</span>
        </div>

        {imagenes.length > 1 && (
          <>
            <button className="lightbox-nav-btn lightbox-prev" onClick={prev} aria-label="Foto anterior">
              <i className="fas fa-chevron-left"></i>
            </button>
            <button className="lightbox-nav-btn lightbox-next" onClick={next} aria-label="Foto siguiente">
              <i className="fas fa-chevron-right"></i>
            </button>
          </>
        )}

        {imagenes.length > 1 && (
          <div className="lightbox-thumbnails">
            {imagenes.map((img, i) => (
              <button
                key={i}
                type="button"
                className={`lightbox-thumb-btn ${i === idx ? 'active' : ''}`}
                onClick={() => setIdx(i)}
                aria-label={`Ver foto ${i + 1}`}
              >
                <img src={img} alt={`Miniatura ${i + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────── MODAL DETALLE DE ALOJAMIENTO ───────────────
function ModalDetalle({ propiedad, onClose }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [modalCheckIn, setModalCheckIn] = useState('');
  const [modalCheckOut, setModalCheckOut] = useState('');
  const [modalGuests, setModalGuests] = useState('2');
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  if (!propiedad) return null;

  const galeria = Array.isArray(propiedad.galeria) && propiedad.galeria.length > 0
    ? propiedad.galeria
    : [propiedad.img];

  let amenidades = [];
  if (Array.isArray(propiedad.amenidades)) {
    amenidades = propiedad.amenidades
      .map(a => typeof a === 'string' ? a.trim().replace(/["[\]]/g, '') : a)
      .filter(a => a && a.length > 0);
  } else if (typeof propiedad.amenidades === 'string' && propiedad.amenidades) {
    amenidades = propiedad.amenidades
      .split(',')
      .map(a => a.trim().replace(/["[\]]/g, ''))
      .filter(a => a && a.length > 0);
  }

  const mapaEmbed = propiedad.mapa_embed || '';
  const srcMatch = mapaEmbed.match(/src="([^"]+)"/);
  const mapSrc = srcMatch ? srcMatch[1] : null;

  const handleBookingClick = (e) => {
    e.preventDefault();
    const datesText = modalCheckIn && modalCheckOut ? ` del ${modalCheckIn} al ${modalCheckOut}` : '';
    const text = `Hola Ernestina, me interesa consultar disponibilidad e información para la estancia *${propiedad.titulo}* (${propiedad.ubicacion})${datesText} para *${modalGuests} personas*. ¿Me podrías compartir detalles?`;
    const url = `https://wa.me/529711924204?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        
        {/* Botón flotante para cerrar */}
        <button className="modal-close-button" onClick={onClose} aria-label="Cerrar detalles">
          <i className="fas fa-xmark"></i>
        </button>

        {/* Hero de fotos del alojamiento */}
        <div className="modal-hero-banner">
          <img 
            src={galeria[activePhotoIdx] || propiedad.img} 
            alt={propiedad.titulo} 
            className="modal-banner-img" 
          />
          <div className="modal-hero-overlay">
            <button 
              className="modal-gallery-badge-btn" 
              onClick={() => setLightboxIdx(activePhotoIdx)}
            >
              <i className="fas fa-images"></i> Ver galería ({galeria.length} fotos)
            </button>
          </div>
        </div>

        {/* Barra de miniaturas */}
        {galeria.length > 1 && (
          <div className="modal-thumbs-row">
            {galeria.slice(0, 8).map((img, i) => (
              <button 
                key={i} 
                type="button"
                className={`modal-thumb-item ${i === activePhotoIdx ? 'active' : ''}`}
                onClick={() => setActivePhotoIdx(i)}
                aria-label={`Seleccionar foto ${i + 1}`}
              >
                <img src={img} alt={`Vista ${i + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        )}

        {/* Contenido principal del modal */}
        <div className="modal-content-area">
          
          <div className="modal-header-info">
            <div className="modal-tags-row">
              <span className="modal-category-tag">{propiedad.categoria}</span>
              <span className="modal-rating-badge">
                <i className="fas fa-star text-gold"></i> {propiedad.rating || '5.0'}
              </span>
              <span className="modal-trust-pill">
                <i className="fas fa-certificate text-green"></i> Alojamiento Verificado
              </span>
            </div>
            <h2 className="modal-property-title">{propiedad.titulo}</h2>
            <p className="modal-property-location">
              <i className="fas fa-location-dot text-terracotta"></i> {propiedad.ubicacion}
            </p>
          </div>

          <div className="modal-columns-grid">

            {/* Columna Izquierda: Información de la propiedad */}
            <div className="modal-left-col">
              
              {/* Tarjetas de capacidad y distribución */}
              <div className="property-specs-grid">
                <div className="spec-stat-card">
                  <i className="fas fa-users-viewfinder"></i>
                  <div>
                    <span className="spec-number">{propiedad.huespedes}</span>
                    <span className="spec-label">Huéspedes</span>
                  </div>
                </div>
                <div className="spec-stat-card">
                  <i className="fas fa-bed"></i>
                  <div>
                    <span className="spec-number">{propiedad.dormitorios}</span>
                    <span className="spec-label">Recámaras</span>
                  </div>
                </div>
                <div className="spec-stat-card">
                  <i className="fas fa-bath"></i>
                  <div>
                    <span className="spec-number">{propiedad.banios}</span>
                    <span className="spec-label">Baños</span>
                  </div>
                </div>
              </div>

              {/* Descripción */}
              <div className="modal-detail-section">
                <h3 className="detail-section-title">
                  <i className="fas fa-circle-info text-terracotta"></i> Sobre esta estancia
                </h3>
                <p className="detail-description-text">{propiedad.descripcion}</p>
              </div>

              {/* Amenidades */}
              {amenidades.length > 0 && (
                <div className="modal-detail-section">
                  <h3 className="detail-section-title">
                    <i className="fas fa-sparkles text-terracotta"></i> Lo que este espacio incluye
                  </h3>
                  <div className="amenities-clean-grid">
                    {amenidades.map((a, i) => (
                      <div key={i} className="amenity-clean-pill">
                        <i className={`fas ${getAmenityIcon(a)}`}></i>
                        <span>{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Galería adicional de fotos */}
              {galeria.length > 1 && (
                <div className="modal-detail-section">
                  <h3 className="detail-section-title">
                    <i className="fas fa-camera text-terracotta"></i> Fotos del alojamiento
                  </h3>
                  <div className="modal-gallery-preview-grid">
                    {galeria.slice(0, 6).map((img, i) => (
                      <div 
                        key={i} 
                        className="gallery-preview-box"
                        onClick={() => setLightboxIdx(i)}
                      >
                        <img src={img} alt={`${propiedad.titulo} ${i + 1}`} loading="lazy" />
                        <div className="gallery-preview-overlay">
                          <i className="fas fa-magnifying-glass-plus"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reglas de la casa y convivencia */}
              <div className="modal-detail-section">
                <h3 className="detail-section-title">
                  <i className="fas fa-house-chimney-window text-terracotta"></i> Convivencia y sugerencias
                </h3>
                <div className="rules-cards-row">
                  <div className="rule-item">
                    <i className="fas fa-clock"></i>
                    <div>
                      <strong>Horarios Flexibles</strong>
                      <p>Check-in y check-out coordinados directamente con anticipación.</p>
                    </div>
                  </div>
                  <div className="rule-item">
                    <i className="fas fa-volume-xmark"></i>
                    <div>
                      <strong>Tranquilidad</strong>
                      <p>Respeto al descanso de vecinos y entorno natural.</p>
                    </div>
                  </div>
                  <div className="rule-item">
                    <i className="fas fa-paw"></i>
                    <div>
                      <strong>Mascotas</strong>
                      <p>Consulta previamente si viajas con animalitos de compañía.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ubicación y Mapa */}
              <div className="modal-detail-section">
                <h3 className="detail-section-title">
                  <i className="fas fa-map-location-dot text-terracotta"></i> Ubicación
                </h3>
                <div className="modal-map-wrap">
                  {mapSrc ? (
                    <iframe
                      title={`Mapa de ${propiedad.ubicacion}`}
                      width="100%"
                      height="240"
                      style={{ border: 0, borderRadius: '12px' }}
                      loading="lazy"
                      src={mapSrc}
                    />
                  ) : (
                    <div className="empty-map-box">
                      <i className="fas fa-location-dot"></i>
                      <p>Zona: <strong>{propiedad.ubicacion}</strong></p>
                    </div>
                  )}
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(propiedad.ubicacion)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-map-link-btn"
                  >
                    <i className="fas fa-arrow-up-right-from-square"></i> Ver ubicación en Google Maps
                  </a>
                </div>
              </div>

            </div>

            {/* Columna Derecha: Tarjeta de consulta con la anfitriona */}
            <div className="modal-right-col">
              <div className="booking-card-clean">
                <div className="booking-card-header">
                  <div>
                    <span className="booking-header-tag">Atención Personal</span>
                    <h3 className="booking-card-title">Consultar Disponibilidad</h3>
                  </div>
                  <div className="booking-rating-pill">
                    <i className="fas fa-star text-gold"></i> {propiedad.rating || '5.0'}
                  </div>
                </div>

                <p className="booking-card-intro">
                  Escríbeme tus fechas estimadas para confirmarte disponibilidad al momento y darte la tarifa exacta.
                </p>

                <div className="booking-inputs-wrap">
                  <div className="booking-dates-row">
                    <div className="booking-input-group">
                      <label><i className="fas fa-calendar-day"></i> Llegada</label>
                      <input 
                        type="date" 
                        value={modalCheckIn}
                        onChange={e => setModalCheckIn(e.target.value)}
                      />
                    </div>
                    <div className="booking-input-group">
                      <label><i className="fas fa-calendar-check"></i> Salida</label>
                      <input 
                        type="date" 
                        value={modalCheckOut}
                        onChange={e => setModalCheckOut(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="booking-input-group">
                    <label><i className="fas fa-user-group"></i> Huéspedes estimados</label>
                    <select 
                      value={modalGuests} 
                      onChange={e => setModalGuests(e.target.value)}
                    >
                      <option value="1">1 persona</option>
                      <option value="2">2 personas</option>
                      <option value="3">3 personas</option>
                      <option value="4">4 personas</option>
                      <option value="5">5 personas</option>
                      <option value="6">6 personas</option>
                      <option value="7">7 personas</option>
                      <option value="8">8 o más personas</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={handleBookingClick}
                  className="btn-whatsapp-primary"
                >
                  <i className="fab fa-whatsapp"></i> Preguntar en WhatsApp
                </button>

                <div className="booking-trust-note">
                  <i className="fas fa-shield-check text-green"></i>
                  <span>Trato directo con la anfitriona · Sin comisiones sorpresa</span>
                </div>

                <div className="booking-perks-list">
                  <div className="perk-bullet">
                    <i className="fas fa-check text-green"></i>
                    <span>Respuesta rápida y asesoría honesta</span>
                  </div>
                  <div className="perk-bullet">
                    <i className="fas fa-check text-green"></i>
                    <span>Coordinación directa de entrega de llaves</span>
                  </div>
                  <div className="perk-bullet">
                    <i className="fas fa-check text-green"></i>
                    <span>Sugerencias locales para tu estancia</span>
                  </div>
                </div>

                <div className="disclaimer-bubble">
                  <i className="fas fa-circle-info"></i>
                  <small>
                    Servicio de intermediación y enlace independiente. Cada estancia se confirma directamente con los encargados de la propiedad.
                  </small>
                </div>

              </div>
            </div>

          </div>
        </div>
      </div>

      {lightboxIdx !== null && (
        <Lightbox
          imagenes={galeria}
          indiceInicial={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

// ─────────────── SECCIÓN FAQ (PREGUNTAS FRECUENTES) ───────────────
function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);

  const faqs = [
    {
      q: '¿Cómo confirmo la disponibilidad y aparto mis fechas?',
      a: 'Nos escribes por WhatsApp indicando la propiedad que te gusta y tus fechas de viaje. Te confirmamos disponibilidad de inmediato y te indicamos el procedimiento sencillo de apartado.'
    },
    {
      q: '¿Quién me recibe al llegar a Huatulco?',
      a: 'Nosotros coordinamos tu llegada con el encargado o anfitrión directo del inmueble, quien te recibirá en la propiedad para entregarte las llaves, mostrarte las instalaciones y asegurarse de que todo esté en orden.'
    },
    {
      q: '¿Las fotos y amenidades corresponden a la realidad?',
      a: 'Sí, todas las fotografías corresponden a las casas y departamentos en su estado actual. Conocemos los espacios para garantizar que tu estancia sea confortable y transparente.'
    },
    {
      q: '¿Por qué conviene reservar por este medio en lugar de aplicaciones?',
      a: 'Porque cuentas con atención personalizada de una persona real que conoce Huatulco, sin pagar comisiones excesivas que inflan el precio en plataformas masivas y con comunicación directa ante cualquier duda.'
    },
    {
      q: '¿Qué facilidades hay si requiero recomendaciones de la zona?',
      a: 'Con mucho gusto te orientamos sobre las mejores playas según el oleaje, transportación local, paseos en lancha a las bahías y lugares recomendados para comer comida oaxaqueña.'
    }
  ];

  return (
    <section className="faq-section" id="faq">
      <div className="section-container">
        <div className="section-intro">
          <span className="section-eyebrow">
            <i className="fas fa-circle-question text-terracotta"></i> Dudas Comunes
          </span>
          <h2 className="section-heading">Preguntas Frecuentes</h2>
          <p className="section-desc">
            Información clara y transparente para que planifiques tus vacaciones con total tranquilidad.
          </p>
        </div>

        <div className="faq-accordion-list">
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className={`faq-item ${openIdx === i ? 'open' : ''}`}
            >
              <button 
                className="faq-question-btn"
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                aria-expanded={openIdx === i}
              >
                <span>{faq.q}</span>
                <i className={`fas fa-chevron-${openIdx === i ? 'up' : 'down'}`}></i>
              </button>
              {openIdx === i && (
                <div className="faq-answer-content">
                  <p>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────── COMPONENTE PRINCIPAL (APP) ───────────────
function App() {
  const [filtro, setFiltro] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [guestFilter, setGuestFilter] = useState('any');
  const [propiedadSeleccionada, setPropiedadSeleccionada] = useState(null);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [propiedades, setPropiedades] = useState(() => getCachedPropiedades());
  const [heroIdx, setHeroIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  // Filtros rápidos del Hero
  const [heroCategory, setHeroCategory] = useState('Todos');
  const [heroGuests, setHeroGuests] = useState('any');

  // Formulario de contacto
  const [selectedHouse, setSelectedHouse] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [guestsCount, setGuestsCount] = useState('2');
  const [contactMessage, setContactMessage] = useState('');

  const handleHeroSearch = (e) => {
    e.preventDefault();
    setFiltro(heroCategory);
    setGuestFilter(heroGuests);
    const catalogEl = document.getElementById('catalogo');
    if (catalogEl) {
      catalogEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const housePart = selectedHouse ? `en la estancia *${selectedHouse}*` : 'en uno de los alojamientos en Huatulco';
    const datesPart = (checkInDate && checkOutDate) ? ` del *${checkInDate}* al *${checkOutDate}*` : '';
    const guestsPart = ` para *${guestsCount} personas*`;
    const messagePart = contactMessage ? `\n\n*Nota o consulta adicional:* ${contactMessage}` : '';

    const text = `Hola Ernestina, me gustaría consultar información y disponibilidad ${housePart}${datesPart}${guestsPart}.${messagePart}`;
    const url = `https://wa.me/529711924204?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const categorias = [
    { label: 'Todos', icon: 'fa-house' },
    { label: 'Playa', icon: 'fa-umbrella-beach' },
    { label: 'Bosque', icon: 'fa-tree' },
    { label: 'Ciudad', icon: 'fa-city' }
  ];

  // Cargar propiedades desde el servicio
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        const data = await getPropiedades();
        if (isMounted && data && data.length > 0) {
          setPropiedades(data);
        }
      } catch (err) {
        console.error('Error cargando propiedades:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, []);

  // Rotación del Hero Slider
  useEffect(() => {
    if (propiedades.length === 0) return;
    const interval = setInterval(() => {
      setHeroIdx(i => (i + 1) % Math.min(propiedades.length, 5));
    }, 6500);
    return () => clearInterval(interval);
  }, [propiedades.length]);

  // Detección de Scroll para el Navbar
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setHeaderScrolled(window.scrollY > 30);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Bloquear scroll si hay modal o menú abierto
  useEffect(() => {
    document.body.style.overflow = (propiedadSeleccionada || menuAbierto) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [propiedadSeleccionada, menuAbierto]);

  // Filtrado de propiedades
  const filtradas = propiedades.filter(p => {
    const matchCategory = filtro === 'Todos' || p.categoria === filtro;
    const matchSearch = !searchQuery || 
      p.titulo.toLowerCase().includes(searchQuery.toLowerCase()) || 
      p.ubicacion.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchGuests = true;
    if (guestFilter === '1-2') matchGuests = parseInt(p.huespedes, 10) <= 2;
    if (guestFilter === '3-4') matchGuests = parseInt(p.huespedes, 10) >= 3 && parseInt(p.huespedes, 10) <= 4;
    if (guestFilter === '5+') matchGuests = parseInt(p.huespedes, 10) >= 5;

    return matchCategory && matchSearch && matchGuests;
  });

  const heroPropiedades = propiedades.slice(0, 5);

  const scrollToSection = (id) => {
    setMenuAbierto(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  return (
    <div className="site-wrapper">

      {/* ═══════════════════ HEADER / BARRA SUPERIOR ═══════════════════ */}
      <header className={`main-header ${headerScrolled ? 'scrolled' : ''}`}>
        <div className="header-inner">
          <a 
            href="#inicio" 
            className="header-brand-wrap" 
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <img 
              src="/logo-nombre.png" 
              alt="Casas María Ernestina" 
              className="header-brand-logo" 
            />
            <div className="brand-text">
              <span className="brand-title">María Ernestina</span>
              <span className="brand-subtitle">Estancias en Huatulco · Trato Directo</span>
            </div>
          </a>

          <nav className="header-nav-menu">
            <a href="#catalogo" className="nav-link">Alojamientos</a>
            <a href="#como-funciona" className="nav-link">Cómo Funciona</a>
            <a href="#sobre-mi" className="nav-link">Sobre Mí</a>
            <a href="#testimonios" className="nav-link">Opiniones</a>
            <a href="#faq" className="nav-link">Preguntas</a>
            <a href="#contacto" className="nav-link">Contacto</a>
          </nav>

          <div className="header-actions-wrap">
            <a
              href="https://wa.me/529711924204?text=Hola%20Ernestina,%20me%20gustar%C3%ADa%20consultar%20informaci%C3%B3n%20sobre%20las%20estancias%20vacacionales"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-header-wa"
            >
              <i className="fab fa-whatsapp"></i>
              <span>Escribir por WhatsApp</span>
            </a>

            <button
              className={`hamburger-btn ${menuAbierto ? 'active' : ''}`}
              onClick={() => setMenuAbierto(!menuAbierto)}
              aria-label="Abrir menú de navegación"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>

      {/* ═══════════════════ MENÚ MÓVIL DESLIZABLE ═══════════════════ */}
      {menuAbierto && <div className="mobile-backdrop" onClick={() => setMenuAbierto(false)} />}

      <div className={`mobile-drawer ${menuAbierto ? 'open' : ''}`}>
        <div className="drawer-header">
          <div className="drawer-brand">
            <img 
              src="/logo-nombre.png" 
              alt="María Ernestina" 
              className="drawer-logo-img" 
            />
            <span>María Ernestina</span>
          </div>
          <button className="drawer-close" onClick={() => setMenuAbierto(false)} aria-label="Cerrar menú">
            <i className="fas fa-xmark"></i>
          </button>
        </div>

        <div className="drawer-body">
          <div className="drawer-links">
            <a href="#catalogo" onClick={() => scrollToSection('catalogo')}>
              <i className="fas fa-house"></i> Alojamientos Disponibles
            </a>
            <a href="#como-funciona" onClick={() => scrollToSection('como-funciona')}>
              <i className="fas fa-list-check"></i> Cómo Funciona
            </a>
            <a href="#sobre-mi" onClick={() => scrollToSection('sobre-mi')}>
              <i className="fas fa-hand-holding-heart"></i> Sobre Mí & Atención
            </a>
            <a href="#testimonios" onClick={() => scrollToSection('testimonios')}>
              <i className="fas fa-heart"></i> Por Qué Trato Directo
            </a>
            <a href="#faq" onClick={() => scrollToSection('faq')}>
              <i className="fas fa-circle-question"></i> Preguntas Frecuentes
            </a>
            <a href="#contacto" onClick={() => scrollToSection('contacto')}>
              <i className="fas fa-envelope"></i> Contacto y Cotización
            </a>
          </div>

          <div className="drawer-contact-card">
            <span className="drawer-contact-tag">Atención Personal</span>
            <p>Comunícate directamente para cualquier duda sobre disponibilidad o servicios.</p>
            <a 
              href="https://wa.me/529711924204?text=Hola%20Ernestina,%20me%20gustar%C3%ADa%20consultar%20disponibilidad"
              target="_blank"
              rel="noopener noreferrer"
              className="drawer-btn-wa"
            >
              <i className="fab fa-whatsapp"></i> Chat en WhatsApp
            </a>
            <a href="tel:+529711924204" className="drawer-btn-tel">
              <i className="fas fa-phone"></i> +52 971-192-4204
            </a>
          </div>
        </div>
      </div>

      {/* ═══════════════════ HERO SECTION (CÁLIDO & LUMINOSO) ═══════════════════ */}
      <section className="hero-section" id="inicio">
        <div className="hero-slider-bg">
          {heroPropiedades.map((p, i) => (
            <div 
              key={p.id} 
              className={`hero-slide ${i === heroIdx ? 'active' : ''}`}
            >
              <img src={p.img} alt={p.titulo} />
            </div>
          ))}
          {heroPropiedades.length === 0 && (
            <div className="hero-slide active fallback-bg" />
          )}
        </div>

        <div className="hero-tint-overlay" />

        <div className="hero-inner-container">
          <div className="hero-badge-pill">
            <i className="fas fa-umbrella-beach text-terracotta"></i>
            <span>Huatulco, Oaxaca · Atención Directa y Personalizada</span>
          </div>

          <h1 className="hero-main-title">
            Tu descanso en Huatulco con la <em>tranquilidad</em> de un trato directo
          </h1>

          <p className="hero-main-desc">
            Te ayudo a elegir y coordinar la casa o departamento ideal para tus vacaciones en la costa de Oaxaca. Sin comisiones excesivas de plataformas ni intermediarios impersonales.
          </p>

          {/* Barra de búsqueda y filtrado rápido */}
          <form onSubmit={handleHeroSearch} className="hero-filter-bar">
            <div className="filter-field-col">
              <label><i className="fas fa-compass"></i> Tipo de Estancia</label>
              <select 
                value={heroCategory} 
                onChange={e => setHeroCategory(e.target.value)}
              >
                <option value="Todos">Todos los destinos</option>
                <option value="Playa">Playa & Costa</option>
                <option value="Bosque">Naturaleza</option>
                <option value="Ciudad">Centro / Ciudad</option>
              </select>
            </div>

            <div className="filter-divider" />

            <div className="filter-field-col">
              <label><i className="fas fa-users"></i> Huéspedes</label>
              <select 
                value={heroGuests} 
                onChange={e => setHeroGuests(e.target.value)}
              >
                <option value="any">Cualquier capacidad</option>
                <option value="1-2">1 - 2 personas</option>
                <option value="3-4">3 - 4 personas</option>
                <option value="5+">5 o más personas</option>
              </select>
            </div>

            <button type="submit" className="hero-filter-submit">
              <i className="fas fa-magnifying-glass"></i>
              <span>Ver Alojamientos</span>
            </button>
          </form>

          {/* Pilares de confianza rápidos */}
          <div className="hero-highlights-row">
            <div className="highlight-pill">
              <i className="fas fa-handshake text-terracotta"></i>
              <span>Trato de persona a persona</span>
            </div>
            <div className="highlight-pill">
              <i className="fas fa-house-circle-check text-green"></i>
              <span>Alojamientos verificados</span>
            </div>
            <div className="highlight-pill">
              <i className="fab fa-whatsapp text-green"></i>
              <span>Coordinación directa por WhatsApp</span>
            </div>
          </div>
        </div>

        {heroPropiedades.length > 1 && (
          <div className="hero-dots-row">
            {heroPropiedades.map((_, i) => (
              <button
                key={i}
                className={`hero-dot-btn ${i === heroIdx ? 'active' : ''}`}
                onClick={() => setHeroIdx(i)}
                aria-label={`Ir a foto ${i + 1}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* ═══════════════════ CÓMO FUNCIONA EL TRATO DIRECTO ═══════════════════ */}
      <section className="how-it-works-section" id="como-funciona">
        <div className="section-container">
          <div className="section-intro">
            <span className="section-eyebrow">
              <i className="fas fa-route text-terracotta"></i> Proceso Transparente
            </span>
            <h2 className="section-heading">¿Cómo funciona el trato directo?</h2>
            <p className="section-desc">
              Sin procesos automáticos complicados ni tarifas ocultas. Planear tu estancia es simple, claro y confiable.
            </p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number-pill">1</div>
              <div className="step-icon-wrap">
                <i className="fas fa-images"></i>
              </div>
              <h3>Explora y Elige</h3>
              <p>Revisa las fotos reales, capacidades y amenidades de cada casa o loft disponible en Huatulco.</p>
            </div>

            <div className="step-card">
              <div className="step-number-pill">2</div>
              <div className="step-icon-wrap">
                <i className="fab fa-whatsapp"></i>
              </div>
              <h3>Escríbeme tus Fechas</h3>
              <p>Mándame un mensaje con las fechas de tu viaje. Te confirmo disponibilidad al momento y te resuelvo dudas.</p>
            </div>

            <div className="step-card">
              <div className="step-number-pill">3</div>
              <div className="step-icon-wrap">
                <i className="fas fa-key"></i>
              </div>
              <h3>Coordina tu Llegada</h3>
              <p>Te enlazo directamente con el encargado de la propiedad para entregarte las llaves y darte la bienvenida.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ CATÁLOGO DE PROPIEDADES ═══════════════════ */}
      <section className="catalog-section" id="catalogo">
        <div className="section-container">
          <div className="section-intro">
            <span className="section-eyebrow">
              <i className="fas fa-door-open text-terracotta"></i> Espacios Cuidados
            </span>
            <h2 className="section-heading">Alojamientos Disponibles</h2>
            <p className="section-desc">
              Selección de departamentos, lofts y casas en Huatulco listos para recibirte.
            </p>
          </div>

          {/* Filtros de Categoría y Búsqueda */}
          <div className="catalog-filters-bar">
            <div className="category-tabs-group">
              {categorias.map(cat => (
                <button
                  key={cat.label}
                  className={`category-tab-btn ${filtro === cat.label ? 'active' : ''}`}
                  onClick={() => setFiltro(cat.label)}
                >
                  <i className={`fas ${cat.icon}`}></i>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>

            <div className="search-input-box">
              <i className="fas fa-magnifying-glass"></i>
              <input 
                type="text" 
                placeholder="Buscar por zona o nombre (ej. Punta Gaviota, playa)..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="clear-search">✕</button>
              )}
            </div>
          </div>

          {/* Estado de carga */}
          {loading ? (
            <div className="properties-cards-grid">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="property-skeleton-card">
                  <div className="skeleton-thumb" />
                  <div className="skeleton-content">
                    <div className="skeleton-line short" />
                    <div className="skeleton-line medium" />
                    <div className="skeleton-line long" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="properties-cards-grid">
              {filtradas.length === 0 ? (
                <div className="empty-catalog-state">
                  <i className="fas fa-house-crack"></i>
                  <h3>No encontramos alojamientos con ese criterio</h3>
                  <p>Prueba limpiando los filtros de búsqueda para ver todas las opciones disponibles.</p>
                  <button 
                    onClick={() => { setFiltro('Todos'); setSearchQuery(''); setGuestFilter('any'); }}
                    className="btn-reset-filters-clean"
                  >
                    Ver todas las estancias
                  </button>
                </div>
              ) : (
                filtradas.map(p => (
                  <div key={p.id} className="property-clean-card">
                    {/* Imagen principal con badge */}
                    <div 
                      className="property-card-photo-wrap"
                      onClick={() => setPropiedadSeleccionada(p)}
                    >
                      <img src={p.img} alt={p.titulo} loading="lazy" />
                      <div className="photo-category-badge">{p.categoria}</div>
                      <div className="photo-rating-badge">
                        <i className="fas fa-star text-gold"></i> {p.rating || '5.0'}
                      </div>
                      <div className="photo-hover-hint">
                        <span><i className="fas fa-images"></i> Ver fotos y detalles</span>
                      </div>
                    </div>

                    {/* Cuerpo de la tarjeta */}
                    <div className="property-card-info">
                      <span className="property-card-location">
                        <i className="fas fa-location-dot text-terracotta"></i> {p.ubicacion}
                      </span>

                      <h3 
                        className="property-card-title-link"
                        onClick={() => setPropiedadSeleccionada(p)}
                      >
                        {p.titulo}
                      </h3>

                      <p className="property-card-summary">
                        {p.descripcion}
                      </p>

                      {/* Pastillas de especificaciones clave */}
                      <div className="property-specs-pills-row">
                        <span className="spec-pill-item" title="Capacidad de huéspedes">
                          <i className="fas fa-users"></i> {p.huespedes} personas
                        </span>
                        <span className="spec-pill-item" title="Recámaras">
                          <i className="fas fa-bed"></i> {p.dormitorios} rec.
                        </span>
                        <span className="spec-pill-item" title="Baños">
                          <i className="fas fa-bath"></i> {p.banios} baños
                        </span>
                      </div>

                      {/* Botones de acción */}
                      <div className="property-card-actions">
                        <button 
                          className="btn-card-details-clean"
                          onClick={() => setPropiedadSeleccionada(p)}
                        >
                          Ver Detalles <i className="fas fa-arrow-right"></i>
                        </button>
                        
                        <a
                          href={`https://wa.me/529711924204?text=Hola%20Ernestina,%20me%20interesa%20consultar%20disponibilidad%20de%20la%20estancia%20*${encodeURIComponent(p.titulo)}*%20en%20*${encodeURIComponent(p.ubicacion)}*.%20%C2%BFMe%20podr%C3%ADas%20brindar%20informes?`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-card-whatsapp-direct"
                          title="Preguntar en WhatsApp"
                          aria-label="Preguntar en WhatsApp"
                        >
                          <i className="fab fa-whatsapp"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════ SOBRE LA ANFITRIONA & ATENCIÓN ═══════════════════ */}
      <section className="about-host-section" id="sobre-mi">
        <div className="section-container">
          <div className="about-host-grid">
            <div className="about-host-text-col">
              <span className="section-eyebrow">
                <i className="fas fa-heart text-terracotta"></i> Atención Personal
              </span>
              <h2 className="section-heading">Hospitalidad honesta, de persona a persona</h2>
              <p className="lead-paragraph">
                Planear tus vacaciones debe ser una experiencia grata desde el primer contacto. Mi labor es acompañarte a encontrar el espacio que mejor se adapte a ti y a tus acompañantes.
              </p>
              <p className="normal-paragraph">
                No soy una empresa multinacional ni una plataforma con respuestas prefabricadas. Conozco personalmente cada una de las casas y departamentos en Huatulco que comparto aquí. Me aseguro de que estén en condiciones óptimas, limpios y listos para tu estancia.
              </p>

              <div className="host-features-checklist">
                <div className="host-feature-row">
                  <div className="feature-check-icon"><i className="fas fa-check"></i></div>
                  <div>
                    <strong>Selección Cuidadosa:</strong>
                    <p>Alojamientos revisados para que tu descanso y privacidad estén garantizados.</p>
                  </div>
                </div>
                <div className="host-feature-row">
                  <div className="feature-check-icon"><i className="fas fa-check"></i></div>
                  <div>
                    <strong>Comunicación Directa:</strong>
                    <p>Cualquier duda sobre la ubicación, accesos o servicios la resolvemos directamente por WhatsApp.</p>
                  </div>
                </div>
                <div className="host-feature-row">
                  <div className="feature-check-icon"><i className="fas fa-check"></i></div>
                  <div>
                    <strong>Sugerencias de la Costa:</strong>
                    <p>Recomendaciones reales de playas, lancheros de confianza y gastronomía local.</p>
                  </div>
                </div>
              </div>

              <div className="host-cta-row">
                <a 
                  href="https://wa.me/529711924204?text=Hola%20Ernestina,%20me%20gustar%C3%ADa%20platicar%20sobre%20opciones%20de%20estancia%20para%20mis%20vacaciones"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-host-wa"
                >
                  <i className="fab fa-whatsapp"></i> Hablar con Ernestina
                </a>
                <a href="tel:+529711924204" className="btn-host-tel">
                  <i className="fas fa-phone"></i> Llamada Directa
                </a>
              </div>
            </div>

            <div className="about-host-visual-col">
              <div className="host-visual-card">
                <img 
                  src={propiedades[0]?.img || 'https://res.cloudinary.com/dkrzgitcl/image/upload/v1771685981/casas-ernestina/fyhqmdx78fhjq3bxvd18.jpg'} 
                  alt="Huatulco vista" 
                  className="host-main-img" 
                  loading="lazy" 
                />
                <div className="host-floating-badge">
                  <div className="floating-badge-icon">
                    <i className="fas fa-sun"></i>
                  </div>
                  <div>
                    <strong>Costa de Huatulco</strong>
                    <span>Oaxaca, México</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ PILARES Y OPINIONES ═══════════════════ */}
      <TestimonialsSection />

      {/* ═══════════════════ PREGUNTAS FRECUENTES ═══════════════════ */}
      <FAQSection />

      {/* ═══════════════════ CONTACTO Y SOLICITUD ═══════════════════ */}
      <section className="contact-section" id="contacto">
        <div className="section-container">
          <div className="contact-layout-grid">
            
            <div className="contact-info-block">
              <span className="section-eyebrow">
                <i className="fas fa-envelope-open-text text-terracotta"></i> Comunicación Sencilla
              </span>
              <h2 className="section-heading">¿Tienes en mente una fecha para viajar?</h2>
              <p className="section-desc">
                Escríbeme directamente por WhatsApp o llena el siguiente formulario para armar tu consulta con las fechas y personas estimadas. Con gusto te respondo a la brevedad.
              </p>

              <div className="contact-cards-list">
                <a 
                  href="https://wa.me/529711924204?text=Hola%20Ernestina,%20me%20interesa%20consultar%20disponibilidad%20para%20mis%20vacaciones"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-channel-item"
                >
                  <div className="channel-icon wa-icon">
                    <i className="fab fa-whatsapp"></i>
                  </div>
                  <div>
                    <span className="channel-label">WhatsApp Directo</span>
                    <strong>+52 971 192 4204</strong>
                    <small>Respuesta rápida y atención personalizada</small>
                  </div>
                </a>

                <a 
                  href="tel:+529711924204"
                  className="contact-channel-item"
                >
                  <div className="channel-icon tel-icon">
                    <i className="fas fa-phone"></i>
                  </div>
                  <div>
                    <span className="channel-label">Llamada Telefónica</span>
                    <strong>+52 971 192 4204</strong>
                    <small>Atención en horario diurno</small>
                  </div>
                </a>

                <div className="contact-channel-item static">
                  <div className="channel-icon loc-icon">
                    <i className="fas fa-location-dot"></i>
                  </div>
                  <div>
                    <span className="channel-label">Destino</span>
                    <strong>Bahías de Huatulco, Oaxaca</strong>
                    <small>Playa, naturaleza y calidez costera</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-block">
              <div className="contact-clean-card">
                <div className="form-card-title-wrap">
                  <h3>Cotizar Estancia Vacacional</h3>
                  <p>Te responderé directamente con las opciones y tarifas disponibles.</p>
                </div>

                <form onSubmit={handleFormSubmit} className="contact-form-inner">
                  <div className="form-field-item">
                    <label><i className="fas fa-house"></i> Alojamiento de Interés</label>
                    <select
                      value={selectedHouse}
                      onChange={e => setSelectedHouse(e.target.value)}
                    >
                      <option value="">Cualquier opción disponible / Asesoría general</option>
                      {propiedades.map(p => (
                        <option key={p.id} value={p.titulo}>
                          {p.titulo} ({p.ubicacion})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-fields-row">
                    <div className="form-field-item">
                      <label><i className="fas fa-calendar-day"></i> Fecha de Llegada</label>
                      <input
                        type="date"
                        value={checkInDate}
                        onChange={e => setCheckInDate(e.target.value)}
                      />
                    </div>
                    <div className="form-field-item">
                      <label><i className="fas fa-calendar-check"></i> Fecha de Salida</label>
                      <input
                        type="date"
                        value={checkOutDate}
                        onChange={e => setCheckOutDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-field-item">
                    <label><i className="fas fa-users"></i> Número de Personas</label>
                    <select
                      value={guestsCount}
                      onChange={e => setGuestsCount(e.target.value)}
                    >
                      <option value="1">1 persona</option>
                      <option value="2">2 personas</option>
                      <option value="3">3 personas</option>
                      <option value="4">4 personas</option>
                      <option value="5">5 personas</option>
                      <option value="6">6 personas</option>
                      <option value="7">7 personas</option>
                      <option value="8">8 o más personas</option>
                    </select>
                  </div>

                  <div className="form-field-item">
                    <label><i className="fas fa-comment-dots"></i> Pregunta o detalle especial (Opcional)</label>
                    <textarea
                      rows="3"
                      placeholder="Ej. ¿Aceptan mascotas? ¿Cuentan con espacio para estacionar camioneta?"
                      value={contactMessage}
                      onChange={e => setContactMessage(e.target.value)}
                    />
                  </div>

                  <button type="submit" className="btn-submit-whatsapp-large">
                    <i className="fab fa-whatsapp"></i> Enviar Consulta a WhatsApp
                  </button>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════ PIE DE PÁGINA CON DESLINDE FORMAL ═══════════════════ */}
      <footer className="main-footer">
        <div className="section-container">
          
          <div className="footer-top-row">
            <div className="footer-identity-col">
              <div className="footer-brand-title">
                <img 
                  src="/logo-nombre.png" 
                  alt="Casas María Ernestina" 
                  className="footer-logo-img" 
                />
                <span>María Ernestina</span>
              </div>
              <p className="footer-brand-tagline">
                Enlace y atención directa para alojamientos y casas vacacionales en Huatulco, Oaxaca.
              </p>
              <div className="footer-contact-pills">
                <a href="https://wa.me/529711924204" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-whatsapp"></i> WhatsApp: +52 971 192 4204
                </a>
                <a href="tel:+529711924204">
                  <i className="fas fa-phone"></i> Teléfono: +52 971 192 4204
                </a>
              </div>
            </div>

            <div className="footer-nav-col">
              <h4>Navegación</h4>
              <ul>
                <li><a href="#catalogo" onClick={() => scrollToSection('catalogo')}>Alojamientos</a></li>
                <li><a href="#como-funciona" onClick={() => scrollToSection('como-funciona')}>Cómo Funciona</a></li>
                <li><a href="#sobre-mi" onClick={() => scrollToSection('sobre-mi')}>Sobre la Anfitriona</a></li>
                <li><a href="#testimonios" onClick={() => scrollToSection('testimonios')}>Opiniones</a></li>
                <li><a href="#faq" onClick={() => scrollToSection('faq')}>Preguntas Frecuentes</a></li>
                <li><a href="#contacto" onClick={() => scrollToSection('contacto')}>Contacto Directo</a></li>
              </ul>
            </div>

            <div className="footer-nav-col">
              <h4>Destinos en Huatulco</h4>
              <ul>
                <li><a href="#catalogo" onClick={() => { setFiltro('Playa'); scrollToSection('catalogo'); }}>Punta Gaviota</a></li>
                <li><a href="#catalogo" onClick={() => { setFiltro('Playa'); scrollToSection('catalogo'); }}>El Arrocito</a></li>
                <li><a href="#catalogo" onClick={() => { setFiltro('Todos'); scrollToSection('catalogo'); }}>La Crucecita</a></li>
                <li><a href="#catalogo" onClick={() => { setFiltro('Todos'); scrollToSection('catalogo'); }}>Lofts con Vista</a></li>
              </ul>
            </div>
          </div>

          {/* DESLINDE LEGAL Y TRANSPARENCIA (PROTECCIÓN DE LA PERSONA) */}
          <div className="footer-disclaimer-box">
            <div className="disclaimer-icon">
              <i className="fas fa-shield-halved"></i>
            </div>
            <div className="disclaimer-content">
              <strong>Nota Informativa y de Transparencia:</strong>
              <p>
                Casas Maria Ernestina opera como un canal independiente de curaduría, orientación y facilitación de contacto entre huéspedes y propietarios particulares de inmuebles vacacionales en las Bahías de Huatulco y el estado de Oaxaca. No constituye una agencia inmobiliaria corporativa ni una empresa hotelera. Cada estancia, sus tarifas, inventario, disponibilidad y reglas de uso son responsabilidad y acuerdo directo con los propietarios correspondientes. La labor de la anfitriona es brindar asesoría cercana, acompañar en la coordinación de llegada y ofrecer un trato humano, claro y de confianza.
              </p>
            </div>
          </div>

          <div className="footer-bottom-row">
            <p>&copy; {new Date().getFullYear()} Casas Maria Ernestina · Todos los derechos reservados.</p>
            <span>Huatulco, Oaxaca, México</span>
          </div>

        </div>
      </footer>

      {/* ═══════════════════ BOTÓN FLOTANTE DE WHATSAPP ═══════════════════ */}
      <a
        href="https://wa.me/529711924204?text=Hola%20Ernestina,%20me%20gustar%C3%ADa%20consultar%20informaci%C3%B3n%20sobre%20las%20estancias%20vacacionales"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-floating-bubble"
        aria-label="Escribir por WhatsApp a Ernestina"
      >
        <span className="wa-bubble-tooltip">
          ¿Dudas sobre alguna casa? Escríbeme
        </span>
        <i className="fab fa-whatsapp"></i>
      </a>

      {/* ═══════════════════ MODAL DETALLE DE ALOJAMIENTO ═══════════════════ */}
      <ModalDetalle
        propiedad={propiedadSeleccionada}
        onClose={() => setPropiedadSeleccionada(null)}
      />

    </div>
  );
}

export default App;
